import { useEffect, useState } from "react";
import api, { formatApiError, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import ExportButton from "../../components/ExportButton";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Cog, Pencil, FileText, Calculator, Calendar, Save, Lock, Send, CheckCircle2 } from "lucide-react";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function Payroll() {
  const { user } = useAuth();
  const canManage = ["partner", "admin", "manager"].includes(user?.role);
  const isPartner = user?.role === "partner";
  const [month, setMonth] = useState(thisMonth());
  const [records, setRecords] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [workingDays, setWorkingDays] = useState(22);
  const [wdSaving, setWdSaving] = useState(false);
  const [computation, setComputation] = useState(null);
  const [computationOpen, setComputationOpen] = useState(false);

  const load = () => api.get(`/payroll?month=${month}`).then((r) => setRecords(r.data));
  const loadWorkingDays = () =>
    api.get(`/payroll/working-days?month=${month}`).then((r) => setWorkingDays(r.data?.working_days || 22)).catch(() => setWorkingDays(22));

  useEffect(() => { load(); loadWorkingDays(); }, [month]);

  const saveWorkingDays = async () => {
    setWdSaving(true);
    try {
      await api.post("/payroll/working-days", { month, working_days: Number(workingDays) });
      toast.success(`Working days for ${month} set to ${workingDays}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWdSaving(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/generate", { month });
      toast.success(`Generated for ${data.count} employees · WD: ${data.working_days}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editRow) return;
    try {
      const payload = {
        basic: Number(editRow.basic) || 0,
        hra: Number(editRow.hra) || 0,
        special: Number(editRow.special) || 0,
        other: Number(editRow.other) || 0,
        leave_days: Number(editRow.leave_days) || 0,
        unpaid_days: Number(editRow.unpaid_days) || 0,
        paid_leave_days: Number(editRow.paid_leave_days) || 0,
        days_worked: editRow.days_worked !== "" && editRow.days_worked !== undefined && editRow.days_worked !== null ? Number(editRow.days_worked) : undefined,
        working_days: editRow.working_days ? Number(editRow.working_days) : undefined,
        pf: Number(editRow.pf) || 0,
        pt: Number(editRow.pt) || 0,
        tds: Number(editRow.tds) || 0,
        other_deductions: Number(editRow.other_deductions) || 0,
        net_pay: editRow.net_pay !== undefined && editRow.net_pay !== "" ? Number(editRow.net_pay) : undefined,
        notes: editRow.notes || "",
        edit_reason: editRow.edit_reason || "",
      };
      if ((editRow.edit_reason || "").trim().length < 3) { toast.error("Please give a reason for this payroll change"); return; }
      await api.patch(`/payroll/${editRow.id}`, payload);
      toast.success("Updated");
      setEditRow(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const showComputation = async (id) => {
    try {
      const { data } = await api.get(`/payroll/${id}/computation`);
      setComputation(data);
      setComputationOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadSlip = async (rec) => {
    try {
      const resp = await fetch(`${API}/payroll/${rec.id}/slip`, { credentials: "include" });
      if (!resp.ok) throw new Error("Failed to fetch payslip");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslip_${rec.month}_${rec.employee_code || rec.user_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error("Cannot download payslip"); }
  };

  const submitRow = async (rec) => {
    try {
      await api.post(`/payroll/${rec.id}/submit`, { remarks: "" });
      toast.success(`Submitted for ${rec.user_name}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approveRow = async (rec) => {
    try {
      await api.post(`/payroll/${rec.id}/approve`, { remarks: "" });
      toast.success(`Approved & locked: ${rec.user_name}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkSubmit = async () => {
    if (!window.confirm(`Submit ALL draft payroll records for ${month} to Partner for approval?`)) return;
    try {
      const { data } = await api.post("/payroll/bulk-submit", { month });
      toast.success(`Submitted ${data.submitted} record(s) for approval`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkApprove = async () => {
    if (!window.confirm(`Approve & LOCK all submitted payroll for ${month}?`)) return;
    try {
      const { data } = await api.post("/payroll/bulk-approve", { month });
      toast.success(`Approved ${data.approved} record(s) — values are now locked`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalGross = records.reduce((s, r) => s + (r.gross || 0), 0);
  const totalNet = records.reduce((s, r) => s + (r.net_pay || 0), 0);
  const totalDaysWorked = records.reduce((s, r) => s + (r.days_worked || 0), 0);

  return (
    <div className="space-y-6 fade-in" data-testid="payroll-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Payroll</div>
          <h1 className="font-sans text-4xl mt-2">Monthly compensation</h1>
          <p className="text-slate-600 text-sm mt-1">Net Pay = (Monthly Gross ÷ Working Days) × Days Worked − Deductions. Comp-off availed is paid; only true unpaid leave reduces days worked.</p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="eyebrow">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-2 rounded-sm w-40" data-testid="payroll-month"/>
          </div>
          {canManage && (
            <div>
              <Label className="eyebrow flex items-center gap-1"><Calendar className="h-3 w-3"/>Working Days</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input type="number" min="1" max="31" value={workingDays} onChange={(e) => setWorkingDays(e.target.value)} className="rounded-sm w-20" data-testid="payroll-working-days-input"/>
                <Button onClick={saveWorkingDays} disabled={wdSaving} size="sm" variant="outline" className="rounded-sm" data-testid="payroll-save-wd"><Save className="h-3.5 w-3.5 mr-1"/>{wdSaving ? "..." : "Set"}</Button>
              </div>
            </div>
          )}
          <Button onClick={generate} disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="payroll-generate"><Cog className="h-4 w-4 mr-2"/>{busy ? "Computing..." : "Generate / Recompute"}</Button>
          <ExportButton path={`/exports/payroll?month=${month}`} filename={`payroll_${month}.csv`} testId="payroll-export"/>
          <Button variant="outline" onClick={async () => {
            try {
              const resp = await fetch(`${API}/exports/payroll-xlsx?month=${month}`, { credentials: "include" });
              const blob = await resp.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = `payroll_${month}.xlsx`; a.click();
              setTimeout(() => URL.revokeObjectURL(url), 30000);
            } catch { toast.error("Excel export failed"); }
          }} className="rounded-sm" data-testid="payroll-export-xlsx"><FileText className="h-4 w-4 mr-2"/>Excel</Button>
          {canManage && <Button onClick={bulkSubmit} variant="outline" className="rounded-sm border-blue-300 text-blue-700" data-testid="payroll-bulk-submit"><Send className="h-4 w-4 mr-2"/>Submit Month</Button>}
          {isPartner && <Button onClick={bulkApprove} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="payroll-bulk-approve"><CheckCircle2 className="h-4 w-4 mr-2"/>Approve & Lock</Button>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 stagger">
        <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Employees</div><div className="serif-num text-4xl mt-3" data-testid="payroll-emp-count">{records.length}</div></div>
        <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Total Days Worked</div><div className="serif-num text-4xl mt-3">{totalDaysWorked.toFixed(1)}</div></div>
        <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Total Gross</div><div className="serif-num text-4xl mt-3">₹{totalGross.toLocaleString("en-IN")}</div></div>
        <div className="bg-white border border-slate-200 rounded-sm p-6"><div className="eyebrow">Total Net Pay</div><div className="serif-num text-4xl mt-3">₹{totalNet.toLocaleString("en-IN")}</div></div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr>
              <th className="text-left p-4">Code</th>
              <th className="text-left p-4">Name</th>
              <th className="text-right p-4">Gross</th>
              <th className="text-right p-4">WD</th>
              <th className="text-right p-4">Days Worked</th>
              <th className="text-right p-4">Per Day</th>
              <th className="text-right p-4">Paid Lv</th>
              <th className="text-right p-4">Unpaid</th>
              <th className="text-right p-4">LOP</th>
              <th className="text-right p-4">PF/PT/TDS</th>
              <th className="text-right p-4 font-semibold">Net Pay</th>
              <th className="text-left p-4">Status</th>
              <th className="p-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="payroll-table">
            {records.length === 0 && <tr><td colSpan={13} className="p-8 text-center text-slate-500">No payroll for {month}. Click Generate.</td></tr>}
            {records.map((r) => {
              const status = r.approval_status || "draft";
              const locked = status === "approved";
              const badgeCls = locked ? "border-emerald-300 text-emerald-700 bg-emerald-50"
                : status === "submitted" ? "border-blue-300 text-blue-700 bg-blue-50"
                : "border-slate-300 text-slate-600 bg-slate-50";
              return (
              <tr key={r.id} className={`hover:bg-slate-50 ${locked ? "opacity-90" : ""}`}>
                <td className="p-4 font-mono text-xs">{r.employee_code}</td>
                <td className="p-4 font-medium">{r.user_name}</td>
                <td className="p-4 text-right serif-num">₹{(r.gross || 0).toLocaleString("en-IN")}</td>
                <td className="p-4 text-right serif-num">{r.working_days || "—"}</td>
                <td className="p-4 text-right serif-num font-semibold">{r.days_worked ?? "—"}</td>
                <td className="p-4 text-right serif-num text-xs text-slate-500">₹{Math.round(r.per_day_rate || 0).toLocaleString("en-IN")}</td>
                <td className="p-4 text-right serif-num">{r.paid_leave_days ?? r.leave_days ?? 0}</td>
                <td className="p-4 text-right serif-num text-red-700">{r.unpaid_days || 0}</td>
                <td className="p-4 text-right serif-num text-red-700 text-xs">₹{Math.round(r.lop_amount || 0).toLocaleString("en-IN")}</td>
                <td className="p-4 text-right serif-num text-xs text-slate-500">₹{Math.round((r.pf || 0) + (r.pt || 0) + (r.tds || 0) + (r.other_deductions || 0)).toLocaleString("en-IN")}</td>
                <td className="p-4 text-right serif-num font-semibold">₹{(r.net_pay || 0).toLocaleString("en-IN")}</td>
                <td className="p-4">
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border rounded-full inline-flex items-center gap-1 ${badgeCls}`} data-testid={`payroll-status-${r.id}`}>
                    {locked && <Lock className="h-3 w-3"/>}{status}
                  </span>
                  {r.amendment_remarks && <div className="text-[10px] text-amber-700 mt-1" title={r.amendment_remarks}>⚠ amended</div>}
                </td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button onClick={() => showComputation(r.id)} className="text-slate-400 hover:text-slate-900 mr-2" title="View breakdown" data-testid={`payroll-compute-${r.id}`}><Calculator className="h-4 w-4 inline"/></button>
                  <button onClick={() => downloadSlip(r)} className="text-slate-400 hover:text-emerald-700 mr-2" title="Download Payslip" data-testid={`payroll-slip-${r.id}`}><FileText className="h-4 w-4 inline"/></button>
                  {canManage && status === "draft" && (
                    <button onClick={() => submitRow(r)} className="text-blue-600 hover:text-blue-800 mr-2" title="Submit for Partner approval" data-testid={`payroll-submit-${r.id}`}><Send className="h-4 w-4 inline"/></button>
                  )}
                  {isPartner && status === "submitted" && (
                    <button onClick={() => approveRow(r)} className="text-emerald-600 hover:text-emerald-800 mr-2" title="Approve & lock" data-testid={`payroll-approve-${r.id}`}><CheckCircle2 className="h-4 w-4 inline"/></button>
                  )}
                  {canManage && (!locked || isPartner) && <button onClick={() => setEditRow({ ...r, net_pay: "" })} className="text-slate-400 hover:text-slate-900" title={locked && !isPartner ? "Locked — only Partner can edit" : "Edit"} data-testid={`payroll-edit-${r.id}`}><Pencil className="h-4 w-4 inline"/></button>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-sans text-2xl">Edit Payroll · {editRow?.user_name}</DialogTitle></DialogHeader>
          {editRow && (
            <div className="space-y-4">
              <div className="text-xs text-slate-500 uppercase tracking-[0.2em]">Earnings</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Basic</Label><Input type="number" value={editRow.basic || 0} onChange={(e) => setEditRow({ ...editRow, basic: e.target.value })} className="mt-1 rounded-sm" data-testid="payroll-edit-basic"/></div>
                <div><Label className="text-xs">HRA</Label><Input type="number" value={editRow.hra || 0} onChange={(e) => setEditRow({ ...editRow, hra: e.target.value })} className="mt-1 rounded-sm"/></div>
                <div><Label className="text-xs">Special</Label><Input type="number" value={editRow.special || 0} onChange={(e) => setEditRow({ ...editRow, special: e.target.value })} className="mt-1 rounded-sm"/></div>
                <div><Label className="text-xs">Other</Label><Input type="number" value={editRow.other || 0} onChange={(e) => setEditRow({ ...editRow, other: e.target.value })} className="mt-1 rounded-sm"/></div>
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-[0.2em]">Attendance</div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Working Days</Label><Input type="number" min="1" max="31" value={editRow.working_days || 22} onChange={(e) => setEditRow({ ...editRow, working_days: e.target.value })} className="mt-1 rounded-sm" data-testid="payroll-edit-wd"/></div>
                <div><Label className="text-xs">Days Worked</Label><Input type="number" step="0.5" value={editRow.days_worked ?? ""} onChange={(e) => setEditRow({ ...editRow, days_worked: e.target.value })} className="mt-1 rounded-sm" data-testid="payroll-edit-days-worked"/></div>
                <div><Label className="text-xs">Paid Leave Days</Label><Input type="number" step="0.5" value={editRow.paid_leave_days ?? editRow.leave_days ?? 0} onChange={(e) => setEditRow({ ...editRow, paid_leave_days: e.target.value })} className="mt-1 rounded-sm"/></div>
                <div><Label className="text-xs">Unpaid Days</Label><Input type="number" step="0.5" value={editRow.unpaid_days || 0} onChange={(e) => setEditRow({ ...editRow, unpaid_days: e.target.value })} className="mt-1 rounded-sm"/></div>
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-[0.2em]">Deductions</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">PF</Label><Input type="number" value={editRow.pf || 0} onChange={(e) => setEditRow({ ...editRow, pf: e.target.value })} className="mt-1 rounded-sm"/></div>
                <div><Label className="text-xs">PT</Label><Input type="number" value={editRow.pt || 0} onChange={(e) => setEditRow({ ...editRow, pt: e.target.value })} className="mt-1 rounded-sm"/></div>
                <div><Label className="text-xs">TDS</Label><Input type="number" value={editRow.tds || 0} onChange={(e) => setEditRow({ ...editRow, tds: e.target.value })} className="mt-1 rounded-sm"/></div>
                <div><Label className="text-xs">Other Deductions</Label><Input type="number" value={editRow.other_deductions || 0} onChange={(e) => setEditRow({ ...editRow, other_deductions: e.target.value })} className="mt-1 rounded-sm"/></div>
              </div>
              <div><Label className="text-xs">Net Pay Override (auto-computes if blank)</Label><Input type="number" value={editRow.net_pay} onChange={(e) => setEditRow({ ...editRow, net_pay: e.target.value })} className="mt-1 rounded-sm" placeholder="Auto-computed"/></div>
              <div><Label className="text-xs">Notes</Label><Input value={editRow.notes || ""} onChange={(e) => setEditRow({ ...editRow, notes: e.target.value })} className="mt-1 rounded-sm"/></div>
              <div><Label className="text-xs">Reason for change (required — recorded in audit trail)</Label><Input value={editRow.edit_reason || ""} onChange={(e) => setEditRow({ ...editRow, edit_reason: e.target.value })} className="mt-1 rounded-sm" data-testid="payroll-edit-reason"/></div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setEditRow(null)} variant="outline" className="rounded-sm">Cancel</Button>
            <Button onClick={saveEdit} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="payroll-save-edit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Computation Dialog */}
      <Dialog open={computationOpen} onOpenChange={setComputationOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payroll-computation-dialog">
          <DialogHeader><DialogTitle className="font-sans text-2xl">Computation Breakdown</DialogTitle></DialogHeader>
          {computation && (
            <div className="space-y-4 text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                <div className="eyebrow mb-2">Formula</div>
                <div className="font-mono text-xs">{computation.formula}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-slate-200 rounded-sm p-3"><div className="eyebrow text-xs">Monthly Gross</div><div className="serif-num text-2xl mt-1">₹{(computation.monthly_gross || 0).toLocaleString("en-IN")}</div></div>
                <div className="border border-slate-200 rounded-sm p-3"><div className="eyebrow text-xs">Working Days</div><div className="serif-num text-2xl mt-1">{computation.working_days}</div></div>
                <div className="border border-slate-200 rounded-sm p-3"><div className="eyebrow text-xs">Per Day Rate</div><div className="serif-num text-2xl mt-1">₹{(computation.per_day_rate || 0).toLocaleString("en-IN")}</div></div>
                <div className="border border-slate-200 rounded-sm p-3"><div className="eyebrow text-xs">Days Worked</div><div className="serif-num text-2xl mt-1">{computation.days_worked}</div></div>
                <div className="border border-slate-200 rounded-sm p-3"><div className="eyebrow text-xs">Computed Gross Payable</div><div className="serif-num text-xl mt-1">₹{(computation.computed_gross_payable || 0).toLocaleString("en-IN")}</div></div>
                <div className="border border-slate-200 rounded-sm p-3"><div className="eyebrow text-xs">LOP Amount</div><div className="serif-num text-xl mt-1 text-red-700">−₹{(computation.lop_amount || 0).toLocaleString("en-IN")}</div></div>
              </div>
              <div>
                <div className="eyebrow mb-2">Paid Leaves ({computation.paid_leaves?.length || 0})</div>
                {computation.paid_leaves?.length ? (
                  <ul className="border border-slate-200 rounded-sm divide-y divide-slate-100">
                    {computation.paid_leaves.map((l, i) => (
                      <li key={i} className="p-2 text-xs flex justify-between">
                        <span>{l.date} · <span className="text-slate-500">{l.type}</span></span>
                        <span>{l.days} day(s) · <span className="text-slate-500">{l.reason}</span></span>
                      </li>
                    ))}
                  </ul>
                ) : <div className="text-xs text-slate-500">None</div>}
              </div>
              <div>
                <div className="eyebrow mb-2">Unpaid Leaves ({computation.unpaid_leaves?.length || 0})</div>
                {computation.unpaid_leaves?.length ? (
                  <ul className="border border-red-200 rounded-sm divide-y divide-red-100 bg-red-50/30">
                    {computation.unpaid_leaves.map((l, i) => (
                      <li key={i} className="p-2 text-xs flex justify-between">
                        <span>{l.date} · <span className="text-slate-500">{l.type}</span></span>
                        <span className="text-red-700">{l.days} day(s) · <span className="text-slate-500">{l.reason}</span></span>
                      </li>
                    ))}
                  </ul>
                ) : <div className="text-xs text-slate-500">None</div>}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                <div className="eyebrow mb-2">Deductions</div>
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div>PF: <span className="serif-num">₹{computation.deductions.pf}</span></div>
                  <div>PT: <span className="serif-num">₹{computation.deductions.pt}</span></div>
                  <div>TDS: <span className="serif-num">₹{computation.deductions.tds}</span></div>
                  <div>Other: <span className="serif-num">₹{computation.deductions.other_deductions}</span></div>
                  <div className="col-span-4 font-semibold pt-2 border-t border-slate-200">Total: <span className="serif-num">₹{computation.deductions.total}</span></div>
                </div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 flex justify-between items-center">
                <div className="eyebrow text-emerald-900">Net Pay</div>
                <div className="serif-num text-3xl text-emerald-700">₹{(computation.net_pay || 0).toLocaleString("en-IN")}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
